import { Fragment, useCallback, useEffect, useState } from "react";
import { SmartTable } from "@/components/SmartTable";
import { Ban, Calculator, CalendarClock, ChevronDown, FileText, Home, Loader2, Mail, MessageSquare, MoreHorizontal, Pencil, Plus, StickyNote, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { FilterBar, PageHeader } from "@/components/FilterBar";
import { Panel } from "@/components/KpiCard";
import { MatchBadge, PlatformBadge, REGUL_LABELS, StatusBadge } from "@/components/Badges";
import { ReservationDialog } from "@/components/ReservationDialog";
import { AnnulationDialog } from "@/components/AnnulationDialog";
import { SuiviDialog } from "@/components/SuiviDialog";
import { NoteReservation } from "@/components/NoteReservation";
import { ThPercueInput } from "@/components/ThPercueInput";
import { LogementsAVerifier } from "@/components/LogementsAVerifier";
import { useFilters } from "@/context/FilterContext";
import { api, cleanParams, download, errMsg, money } from "@/lib/api";

const Th = ({ children, right }) => <th className={`py-2 px-2 text-[11px] font-mono uppercase tracking-wider text-slate-500 whitespace-nowrap ${right ? "text-right" : "text-left"}`}>{children}</th>;

const RegulSelect = ({ r, onChange }) => (
  <Select value={r.regularisation.statut} onValueChange={(v) => onChange(r, v)}>
    <SelectTrigger className="h-8 w-32 text-xs" data-testid={`regul-select-${r.id}`}><SelectValue /></SelectTrigger>
    <SelectContent>
      {Object.entries(REGUL_LABELS).map(([k, l]) => <SelectItem key={k} value={k} data-testid={`regul-option-${k}`}>{l}</SelectItem>)}
    </SelectContent>
  </Select>
);

const RowMenu = ({ r, onEdit, onDelete, onAnnul }) => {
  const dl = (kind, name) => download(`/reservations/${r.id}/${kind}`, {}, name).catch((e) => toast.error(errMsg(e)));
  const navigate = useNavigate();
  return (
    <div className="flex items-center justify-end">
    <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-emerald-700" title="Vérifier dans le calculateur" aria-label="Vérifier dans le calculateur"
      onClick={() => navigate(`/calcul-reservation?reservation=${r.id}`)} data-testid={`res-calculer-${r.id}`}><Calculator className="h-4 w-4" /></Button>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`res-menu-${r.id}`}><MoreHorizontal className="h-4 w-4" /></Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => dl("rapport-pdf", `rapport_${r.code}.pdf`)} data-testid={`res-rapport-pdf-${r.id}`}><FileText className="h-4 w-4 mr-2" />Rapport d'erreur (PDF)</DropdownMenuItem>
        <DropdownMenuItem onClick={() => dl("lettre-pdf", `lettre_${r.code}.pdf`)} data-testid={`res-lettre-pdf-${r.id}`}><Mail className="h-4 w-4 mr-2" />Lettre de contestation (PDF)</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate(`/calcul-reservation?reservation=${r.id}`)} data-testid={`res-calculer-menu-${r.id}`}><Calculator className="h-4 w-4 mr-2" />Vérifier dans le calculateur</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => onEdit(r)} data-testid={`res-edit-${r.id}`}><Pencil className="h-4 w-4 mr-2" />Modifier</DropdownMenuItem>
        <DropdownMenuItem onClick={() => onAnnul(r)} data-testid={`res-annulation-${r.id}`}><Ban className="h-4 w-4 mr-2" />{r.annulee ? "Annulation par le voyageur…" : "Marquer annulée par le voyageur…"}</DropdownMenuItem>
        <DropdownMenuItem className="text-red-600" onClick={() => onDelete(r)} data-testid={`res-delete-${r.id}`}><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    </div>
  );
};

const Detail = ({ r, onSaved }) => (
  <tr className="bg-slate-50/70" data-st-parent={r.id} data-testid={`res-detail-${r.id}`}>
    <td colSpan={14} className="px-4 py-3">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-xs">
        {[["Taxe d'hébergement 3,5 %", r.taxe_hebergement], ["Base TPS/TVQ", r.base_tps_tvq], ["TPS 5 %", r.tps_attendue], ["TVQ 9,975 %", r.tvq_attendue], ["Commission", r.commission], ["Total versé", r.total_verse]].map(([l, v]) => (
          <div key={l}><p className="text-slate-500">{l}</p><p className="tabular font-semibold">{money(v)}</p></div>
        ))}
      </div>
      {r.plateforme === "Booking" && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs" data-testid={`res-th-detail-bloc-${r.id}`}>
          <span className="text-slate-500">Taxe de séjour perçue par Booking</span>
          <ThPercueInput r={r} onSaved={onSaved} testId={`res-th-detail-${r.id}`} suggestion={Math.round(r.loyer * 3.5) / 100} />
        </div>
      )}
      <p className="mt-2 text-xs text-slate-600">{r.note_comptable}{r.match && ` · Dépôt du ${r.match.depot_date} (${money(r.match.depot_montant)})`}</p>
      <NoteReservation r={r} onSaved={onSaved} />
    </td>
  </tr>
);

const ResRow = ({ r, open, toggle, onRegul, onEdit, onDelete, onSuivi, onAnnul, onSaved }) => (
  <tr className="border-t border-slate-100 hover:bg-slate-50/70" data-st-id={r.id} data-testid={`res-row-${r.id}`}>
    <td className="py-2.5 px-2"><button onClick={toggle} data-testid={`res-expand-${r.id}`}><ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} /></button></td>
    <td className="px-2 tabular text-xs whitespace-nowrap">{r.date}</td>
    <td className="px-2 font-mono text-xs"><span className="inline-flex items-center gap-1">{r.code}{r.note_interne && <StickyNote className="h-3.5 w-3.5 text-amber-600" title={r.note_interne} data-testid={`res-note-icon-${r.id}`} />}</span></td>
    <td className="px-2 text-xs text-slate-700" data-testid={`res-client-${r.id}`}>{r.nom_client || "—"}</td>
    <td className="px-2"><PlatformBadge plateforme={r.plateforme} /></td>
    <td className="px-2 text-xs text-slate-600">{r.logement || "—"}</td>
    <td className="px-2 text-right tabular">{money(r.loyer + r.frais_menage)}</td>
    <td className="px-2 text-right tabular">{money(r.taxes_versees)}</td>
    <td className="px-2 text-right tabular">{money(r.taxes_attendues)}</td>
    <td className="px-2 text-right">{r.plateforme === "Booking" ? <ThPercueInput r={r} onSaved={onSaved} testId={`res-th-${r.id}`} /> : <span className="text-xs text-slate-400">—</span>}</td>
    <td className={`px-2 text-right tabular font-semibold ${r.statut === "ERREUR" && !r.a_venir ? "text-red-600" : "text-slate-700"}`} data-testid={`res-ecart-${r.id}`}>{r.a_venir ? "—" : money(r.ecart)}</td>
    <td className="px-2"><div className="flex flex-col gap-1 items-start">{r.a_venir ? <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-800" data-testid={`res-a-venir-${r.id}`}>À venir</span> : <StatusBadge statut={r.statut} plateforme={r.plateforme} testId={`res-statut-${r.id}`} />}{r.ecart_cause && !r.a_venir && <span className="max-w-[180px] text-[10px] leading-tight text-slate-500" data-testid={`res-cause-${r.id}`}>{r.ecart_cause}</span>}<MatchBadge match={r.match} testId={`res-match-${r.id}`} />{r.annulation_probable && (
      <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] border-amber-300 text-amber-800 hover:bg-amber-50" onClick={() => onAnnul(r)} data-testid={`res-penalite-rapide-${r.id}`}>
        <Ban className="h-3 w-3 mr-1" />Cocher la pénalité
      </Button>
    )}</div></td>
    <td className="px-2">{!r.a_venir ? (
      <div className="flex items-center gap-1">
        {(r.statut === "ERREUR" || r.regularisation.reclamation) && <RegulSelect r={r} onChange={onRegul} />}
        <Button variant="ghost" size="sm" className="h-8 px-2 text-xs text-slate-600" onClick={() => onSuivi(r)} data-testid={`res-suivi-${r.id}`}>
          <MessageSquare className="h-3.5 w-3.5 mr-1" />{r.regularisation.suivis?.length || 0}
          {r.regularisation.relance_date && <CalendarClock className={`h-3.5 w-3.5 ml-1 ${r.regularisation.relance_date <= new Date().toISOString().slice(0, 10) ? "text-amber-600" : "text-slate-400"}`} />}
        </Button>
      </div>
    ) : <span className="text-xs text-slate-400">—</span>}</td>
    <td className="px-2 text-right"><RowMenu r={r} onEdit={onEdit} onDelete={onDelete} onAnnul={onAnnul} /></td>
  </tr>
);

export default function Reservations() {
  const { filters, refreshOptions } = useFilters();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState({});
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [annul, setAnnul] = useState(null);
  const [suiviId, setSuiviId] = useState(null);
  const [reaff, setReaff] = useState(false);

  const load = useCallback(() => {
    api.get("/reservations", { params: cleanParams(filters) }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [filters]);
  useEffect(load, [load]);

  const onRegul = async (r, statut) => {
    try { await api.put(`/reservations/${r.id}/regularisation`, { statut, note: r.regularisation.note || "" }); toast.success(`Statut : ${REGUL_LABELS[statut]}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const onDelete = async (r) => {
    if (!window.confirm(`Supprimer la réservation ${r.code} ?`)) return;
    try { await api.delete(`/reservations/${r.id}`); toast.success("Réservation supprimée"); load(); refreshOptions(); } catch (e) { toast.error(errMsg(e)); }
  };

  const reaffecter = async () => {
    setReaff(true);
    try {
      const { data } = await api.post("/logements/reaffecter");
      const det = Object.entries(data.par_logement).map(([l, n]) => `${l} : ${n}`).join(", ");
      toast.success(data.modifiees ? `${data.modifiees} réservation(s) corrigée(s) d'après les fichiers — ${det}` : "Toutes les réservations ont déjà le bon logement.", { duration: 8000 });
      if (data.annonces_inconnues.length) toast.warning(`Annonce(s) sans logement correspondant : ${data.annonces_inconnues.join(", ")}`, { duration: Infinity, closeButton: true });
      load(); refreshOptions();
    } catch (e) { toast.error(errMsg(e)); }
    setReaff(false);
  };

  if (!rows) return <Loader2 className="h-6 w-6 animate-spin text-emerald-700" />;
  const totalEcart = rows.filter((r) => r.statut === "ERREUR" && !r.a_venir).reduce((s, r) => s + r.ecart, 0);
  const probables = rows.filter((r) => r.annulation_probable);

  return (
    <div data-testid="reservations-page">
      <PageHeader eyebrow="Contrôle fiscal" title="Réservations & taxes">
        <div className="flex flex-col gap-2 items-start xl:items-end">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" disabled={reaff} onClick={reaffecter} title="Relit les fichiers Airbnb / Booking importés et remet chaque réservation (et son dépôt) dans le bon logement" data-testid="reaffecter-logements-button">{reaff ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Home className="h-4 w-4 mr-2" />}Réaffecter les logements</Button>
            <Button onClick={() => setDialog({ open: true, initial: null })} className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-reservation-button"><Plus className="h-4 w-4 mr-2" />Saisie manuelle</Button>
          </div>
          <FilterBar />
        </div>
      </PageHeader>
      <LogementsAVerifier onChange={load} />
      {probables.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" data-testid="annulations-probables">
          <p className="text-sm font-semibold text-amber-900">{probables.length} annulation(s) probable(s) à vérifier</p>
          <p className="text-xs text-amber-800 mb-2">Réservations Airbnb versées sans TPS ni TVQ : souvent une annulation de dernière minute avec pénalité (ex. 50 %).</p>
          <div className="flex flex-wrap gap-2">
            {probables.map((r) => (
              <Button key={r.id} size="sm" variant="outline" className="h-8 bg-white text-xs" onClick={() => setAnnul(r)} data-testid={`annulation-probable-${r.id}`}>
                {r.code} · {r.nom_client || "voyageur"} · {r.date} · {money(r.loyer + r.frais_menage)} — cocher la pénalité
              </Button>
            ))}
          </div>
        </div>
      )}
      <Panel title={`${rows.length} réservation(s) · écart total à régulariser ${money(totalEcart)}`} testId="reservations-panel">
        <div className="overflow-x-auto -mx-5">
          <SmartTable id="reservations" className="w-full text-sm min-w-[1200px]" data-testid="reservations-table">
            <thead><tr><Th /><Th>Date</Th><Th>Code</Th><Th>Client</Th><Th>Plateforme</Th><Th>Logement</Th><Th right>Revenu brut</Th><Th right>Taxes versées</Th><Th right>Attendues</Th><Th right>Taxe séjour perçue</Th><Th right>Écart</Th><Th>Statut</Th><Th>Régularisation</Th><Th /></tr></thead>
            <tbody>
              {rows.map((r) => (
                <Fragment key={r.id}>
                  <ResRow r={r} open={open[r.id]} toggle={() => setOpen({ ...open, [r.id]: !open[r.id] })} onRegul={onRegul} onEdit={(x) => setDialog({ open: true, initial: x })} onDelete={onDelete} onSuivi={(x) => setSuiviId(x.id)} onAnnul={setAnnul} onSaved={load} />
                  {open[r.id] && <Detail r={r} onSaved={load} />}
                </Fragment>
              ))}
              {!rows.length && <tr><td colSpan={14} className="py-6 text-center text-slate-500">Aucune réservation pour ces filtres.</td></tr>}
            </tbody>
          </SmartTable>
        </div>
      </Panel>
      <SuiviDialog r={rows.find((x) => x.id === suiviId) || null} onClose={() => setSuiviId(null)} onChanged={load} />
      <AnnulationDialog r={annul} onClose={() => setAnnul(null)} onDone={() => { setAnnul(null); load(); }} />
      <ReservationDialog open={dialog.open} initial={dialog.initial} onClose={() => setDialog({ open: false, initial: null })} onDone={() => { setDialog({ open: false, initial: null }); load(); refreshOptions(); }} />
    </div>
  );
}
